
import { Component } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { shareStoreActions } from '@/store/share.store';
import ShareConfiguration, { Pricing, PromoterRates } from '@/models/ShareConfiguration';
import { validationMixin } from 'vuelidate';
import { decimal, maxValue, minValue, numeric, required } from 'vuelidate/lib/validators';
import { mixins } from 'vue-class-component';
import ErrorMessageHandlerMixin from '@/misc/ErrorMessageHandler.mixin';
import moment from 'moment';

const ShareStore = namespace('share');

type PricingField = keyof Pricing;
const PRICING_FIELDS: PricingField[] = ['baseFare', 'perKm', 'perMinute', 'bookingFee', 'sharedDiscountPercent'];
/** the trip the card's worked example is computed for */
const PRICING_EXAMPLE_KM = 5;
const PRICING_EXAMPLE_MINUTES = 12;

@Component({
    mixins: [validationMixin],
    validations: {
        share: { required, minValue: minValue(1), maxValue: maxValue(1000), decimal },
        waitingTime: { required, numeric, minValue: minValue(1), maxValue: maxValue(120) },
        ratePerDriver: { required, minValue: minValue(0), maxValue: maxValue(100000), decimal },
        ratePerCustomer: { required, minValue: minValue(0), maxValue: maxValue(100000), decimal },
        signupBonus: { required, minValue: minValue(0), maxValue: maxValue(100000), decimal },
        perPassengerRate: { required, minValue: minValue(0), maxValue: maxValue(100000), decimal },
        // mirrors the server: decimals, nothing negative, the discount a percentage 0 - 100
        pricing: {
            baseFare: { required, decimal, minValue: minValue(0) },
            perKm: { required, decimal, minValue: minValue(0) },
            perMinute: { required, decimal, minValue: minValue(0) },
            bookingFee: { required, decimal, minValue: minValue(0) },
            sharedDiscountPercent: { required, decimal, minValue: minValue(0), maxValue: maxValue(100) }
        }
    }
})
export default class ShareManagementView extends mixins(ErrorMessageHandlerMixin) {
    @ShareStore.Action(shareStoreActions.GET_CONFIGURATION)
    private getConfiguration!: () => Promise<ShareConfiguration>;

    @ShareStore.Action(shareStoreActions.SET_PRICE_PER_RIDE)
    private setShare!: (price: number) => Promise<ShareConfiguration>;

    @ShareStore.Action(shareStoreActions.SET_PRICE_PER_RIDE_ACTIVE)
    private setShareActive!: (active: boolean) => Promise<ShareConfiguration>;

    @ShareStore.Action(shareStoreActions.SET_WAITING_TIME)
    private setWaitingTime!: (minutes: number) => Promise<ShareConfiguration>;

    @ShareStore.Action(shareStoreActions.SET_PROMOTER_RATES)
    private setPromoterRates!: (rates: PromoterRates) => Promise<ShareConfiguration>;

    @ShareStore.Action(shareStoreActions.SET_PROMOTER_INCENTIVE)
    private setPromoterIncentive!: (payload: {
        signupBonus: number, perPassengerRate: number, effectiveFrom?: string | null
    }) => Promise<ShareConfiguration>;

    @ShareStore.Action(shareStoreActions.SET_PRICING)
    private setPricing!: (payload: Pricing) => Promise<ShareConfiguration>;

    /**
     * flag that indicates whether the view is loading
     * @private
     */
    private isLoading: boolean = false;

    /**
     * flag that indicates that an error occurred while loading the share configuration
     * @private
     */
    private errorOnLoading: boolean = false;

    /**
     * flag used to show loading flag inside the confirm dialog of current share
     * @private
     */
    private isLoadingShareDialog: boolean = false;

    /**
     * flag used to show loading flag inside the confirm dialog of share active
     * @private
     */
    private isLoadingShareActiveDialog: boolean = false;

    /**
     * v-model to control the isShareActive state
     * @private
     */
    private isShareActive: boolean = true;

    /**
     * v-model to control the current share
     * @private
     */
    private share: string = '';

    /**
     * v-model to control the driver waiting time (in minutes)
     * @private
     */
    private waitingTime: string = '';

    /**
     * v-models to control the promoter rates
     * @private
     */
    private ratePerDriver: string = '';
    private ratePerCustomer: string = '';

    /**
     * flag to control the confirm dialog opening for share active changes
     * @private
     */
    private showShareActiveConfirmDialog: boolean = false;

    /**
     * flag to control the confirm dialog opening for current share changes
     * @private
     */
    private showCurrentShareConfirmDialog: boolean = false;

    /**
     * flag to control the confirm dialog opening for waiting time changes
     * @private
     */
    private showWaitingTimeConfirmDialog: boolean = false;

    /**
     * flag to control the confirm dialog opening for promoter rate changes
     * @private
     */
    private showPromoterRatesConfirmDialog: boolean = false;

    /**
     * flag used to show loading flag inside the confirm dialog of waiting time
     * @private
     */
    private isLoadingWaitingTimeDialog: boolean = false;

    /**
     * flag used to show loading flag inside the confirm dialog of promoter rates
     * @private
     */
    private isLoadingPromoterRatesDialog: boolean = false;

    /**
     * flag used to show loading flag inside the confirm dialog of the promoter
     * incentive settings
     * @private
     */
    private isLoadingPromoterIncentiveDialog: boolean = false;

    private showPromoterIncentiveConfirmDialog: boolean = false;

    private signupBonus: string = '0';
    private perPassengerRate: string = '0';

    /**
     * Whether the incentive model should be live. Bound to a checkbox rather
     * than a date picker because the only two states that matter are "not live,
     * nothing accrues" and "live from now on".
     */
    private incentiveLive: boolean = false;

    /**
     * v-models of the fare-suggestion pricing, strings like every other input
     * here. Decimals are expected (1.50 is a value), so nothing is parsed as an
     * integer; the discount is a percentage (40 = 40 % off), never a fraction.
     */
    private pricing: Record<PricingField, string> = {
        baseFare: '', perKm: '', perMinute: '', bookingFee: '', sharedDiscountPercent: ''
    };

    /**
     * Field-level messages from the server's 422 answer, shown under the field
     * they belong to. The server is the authority; the client-side rules only
     * mirror it.
     */
    private pricingServerErrors: Partial<Record<PricingField, string>> = {};

    private showPricingConfirmDialog: boolean = false;
    private isLoadingPricingDialog: boolean = false;

    /**
     * configuration loaded from the api
     * @private
     */
    private configuration = new ShareConfiguration();

    private async created() {
        try {
            this.isLoading = true;
            this.configuration = await this.getConfiguration();
            this.isShareActive = this.configuration.pricePerRideActive;
            this.share = this.configuration.pricePerRide.toString();
            this.waitingTime = (this.configuration.driverWaitingTimeMin ?? 4).toString();
            this.ratePerDriver = (this.configuration.promoterRates?.perDriver ?? 0).toString();
            this.ratePerCustomer = (this.configuration.promoterRates?.perCustomer ?? 0).toString();
            this.signupBonus = (this.configuration.promoterIncentive?.signupBonus ?? 0).toString();
            this.perPassengerRate = (this.configuration.promoterIncentive?.perPassengerRate ?? 0).toString();
            this.incentiveLive = !!this.configuration.promoterIncentive?.effectiveFrom;
            this.pricing = this.pricingFromConfiguration();
        } catch (e) {
            this.$notifyErrorSimplified('SHARE.ERROR');
            this.errorOnLoading = true;
        } finally {
            this.isLoading = false;
        }
    }

    private onCloseDialog() {
        this.showShareActiveConfirmDialog = false;
        this.showCurrentShareConfirmDialog = false;
        this.showWaitingTimeConfirmDialog = false;
        this.showPromoterRatesConfirmDialog = false;
        this.showPromoterIncentiveConfirmDialog = false;
        this.showPricingConfirmDialog = false;
    }

    private async onSaveShareActive() {
        this.showShareActiveConfirmDialog = true;
    }

    private async onSaveShareActiveConfirmed() {
        try {
            this.isLoadingShareActiveDialog = true;
            this.configuration = await this.setShareActive(this.isShareActive);
            this.showShareActiveConfirmDialog = false;
            this.$notifySuccessSimplified('SHARE.SHARE_ACTIVE.SUCCESS');
        } catch (e) {
            this.$notifyErrorSimplified('SHARE.SHARE_ACTIVE.ERROR');
        } finally {
            this.isLoadingShareActiveDialog = false;
        }
    }

    private onSaveCurrentShare() {
        this.$v.share!.$touch();
        if (this.$v.share!.$invalid) {
            return;
        }
        this.showCurrentShareConfirmDialog = true;
    }

    private async onSaveCurrentShareConfirmed() {
        try {
            this.isLoadingShareDialog = true;
            const pricePerRide = parseFloat(this.share);
            this.configuration = await this.setShare(pricePerRide);
            this.showCurrentShareConfirmDialog = false;
            this.$notifySuccessSimplified('SHARE.CURRENT_SHARE.SUCCESS');
        } catch (e) {
            this.$notifyErrorSimplified('SHARE.CURRENT_SHARE.ERROR');
        } finally {
            this.isLoadingShareDialog = false;
        }
    }

    private onSaveWaitingTime() {
        this.$v.waitingTime!.$touch();
        if (this.$v.waitingTime!.$invalid) {
            return;
        }
        this.showWaitingTimeConfirmDialog = true;
    }

    private async onSaveWaitingTimeConfirmed() {
        try {
            this.isLoadingWaitingTimeDialog = true;
            const minutes = parseInt(this.waitingTime, 10);
            this.configuration = await this.setWaitingTime(minutes);
            this.waitingTime = (this.configuration.driverWaitingTimeMin ?? minutes).toString();
            this.showWaitingTimeConfirmDialog = false;
            this.$notifySuccessSimplified('SHARE.WAITING_TIME.SUCCESS');
        } catch (e) {
            this.$notifyErrorSimplified('SHARE.WAITING_TIME.ERROR');
        } finally {
            this.isLoadingWaitingTimeDialog = false;
        }
    }

    private onSavePromoterRates() {
        this.$v.ratePerDriver!.$touch();
        this.$v.ratePerCustomer!.$touch();
        if (this.$v.ratePerDriver!.$invalid || this.$v.ratePerCustomer!.$invalid) {
            return;
        }
        this.showPromoterRatesConfirmDialog = true;
    }

    private async onSavePromoterRatesConfirmed() {
        try {
            this.isLoadingPromoterRatesDialog = true;
            const rates: PromoterRates = {
                perDriver: parseFloat(this.ratePerDriver),
                perCustomer: parseFloat(this.ratePerCustomer)
            };
            this.configuration = await this.setPromoterRates(rates);
            this.ratePerDriver = (this.configuration.promoterRates?.perDriver ?? rates.perDriver).toString();
            this.ratePerCustomer = (this.configuration.promoterRates?.perCustomer ?? rates.perCustomer).toString();
            this.showPromoterRatesConfirmDialog = false;
            this.$notifySuccessSimplified('SHARE.PROMOTER_RATES.SUCCESS');
        } catch (e) {
            this.$notifyErrorSimplified('SHARE.PROMOTER_RATES.ERROR');
        } finally {
            this.isLoadingPromoterRatesDialog = false;
        }
    }

    private get hasCurrentShareChanged() {
        return this.configuration?.pricePerRide?.toString() !== this.share;
    }

    private get hasWaitingTimeChanged() {
        return (this.configuration?.driverWaitingTimeMin ?? 4).toString() !== this.waitingTime;
    }

    private get havePromoterRatesChanged() {
        return (this.configuration?.promoterRates?.perDriver ?? 0).toString() !== this.ratePerDriver ||
            (this.configuration?.promoterRates?.perCustomer ?? 0).toString() !== this.ratePerCustomer;
    }

    private onSavePromoterIncentive() {
        this.$v.signupBonus!.$touch();
        this.$v.perPassengerRate!.$touch();
        if (this.$v.signupBonus!.$invalid || this.$v.perPassengerRate!.$invalid) {
            return;
        }
        this.showPromoterIncentiveConfirmDialog = true;
    }

    private async onSavePromoterIncentiveConfirmed() {
        try {
            this.isLoadingPromoterIncentiveDialog = true;
            // Only send effectiveFrom when the live flag is actually being
            // changed, so re-saving the rates of an already-live model does not
            // silently move the go-live date forward and strand everything that
            // accrued before it.
            const alreadyLive = !!this.configuration.promoterIncentive?.effectiveFrom;
            const payload: { signupBonus: number, perPassengerRate: number, effectiveFrom?: string | null } = {
                signupBonus: parseFloat(this.signupBonus),
                perPassengerRate: parseFloat(this.perPassengerRate)
            };
            if (this.incentiveLive !== alreadyLive) {
                payload.effectiveFrom = this.incentiveLive ? new Date().toISOString() : null;
            }

            this.configuration = await this.setPromoterIncentive(payload);
            this.signupBonus = (this.configuration.promoterIncentive?.signupBonus ?? 0).toString();
            this.perPassengerRate = (this.configuration.promoterIncentive?.perPassengerRate ?? 0).toString();
            this.incentiveLive = !!this.configuration.promoterIncentive?.effectiveFrom;
            this.showPromoterIncentiveConfirmDialog = false;
            this.$notifySuccessSimplified('SHARE.PROMOTER_INCENTIVE.SUCCESS');
        } catch (e) {
            this.$notifyErrorSimplified('SHARE.PROMOTER_INCENTIVE.ERROR');
        } finally {
            this.isLoadingPromoterIncentiveDialog = false;
        }
    }

    /**
     * The card's fields from the loaded configuration. A backend that knows the
     * block answers it complete (the defaults when nothing was ever saved); one
     * that does not know it yet leaves the fields empty, and Save stays off.
     */
    private pricingFromConfiguration(): Record<PricingField, string> {
        const stored = this.configuration?.pricing;
        const text = (value?: number) => (value === undefined || value === null) ? '' : value.toString();
        return {
            baseFare: text(stored?.baseFare),
            perKm: text(stored?.perKm),
            perMinute: text(stored?.perMinute),
            bookingFee: text(stored?.bookingFee),
            sharedDiscountPercent: text(stored?.sharedDiscountPercent)
        };
    }

    private onPricingInput(field: PricingField) {
        this.$delete(this.pricingServerErrors, field);
        this.triggerValidation(`pricing.${field}`);
    }

    /**
     * The server's message for the field when its 422 named it, otherwise the
     * client-side validation message.
     */
    private pricingErrorMessages(field: PricingField): string | null {
        return this.pricingServerErrors[field] ?? this.identifyErrorMessage((this.$v.pricing as any)[field]);
    }

    /**
     * A discount between 0 and 1 is almost certainly a fraction typed for a
     * percentage (0.40 meant as 40 %). Valid, so not an error - but flagged.
     */
    private get sharedDiscountWarning(): string[] {
        const value = parseFloat(this.pricing.sharedDiscountPercent);
        return Number.isFinite(value) && value > 0 && value < 1
            ? [this.$t('SHARE.PRICING.SHARED_DISCOUNT_FRACTION_WARNING').toString()]
            : [];
    }

    /**
     * A live worked example from the fields as they are typed: a 5 km,
     * 12-minute trip, private and shared, rounded to whole N$ the way the
     * driver app rounds its suggestion. Null while a field is empty or
     * invalid, so the line never shows a number built on bad input.
     */
    private get pricingExample(): { private: number, shared: number } | null {
        const value = (field: PricingField) => parseFloat(this.pricing[field]);
        const figures = PRICING_FIELDS.map(value);
        if (figures.some((n) => !Number.isFinite(n) || n < 0) || value('sharedDiscountPercent') > 100) {
            return null;
        }
        const total = value('baseFare') + PRICING_EXAMPLE_KM * value('perKm') +
            PRICING_EXAMPLE_MINUTES * value('perMinute') + value('bookingFee');
        return {
            private: Math.round(total),
            shared: Math.round(total * (1 - value('sharedDiscountPercent') / 100))
        };
    }

    private get hasPricingChanged() {
        const loaded = this.pricingFromConfiguration();
        return PRICING_FIELDS.some((field) => loaded[field] !== this.pricing[field]);
    }

    private onSavePricing() {
        PRICING_FIELDS.forEach((field) => (this.$v.pricing as any)[field].$touch());
        if (PRICING_FIELDS.some((field) => (this.$v.pricing as any)[field].$invalid)) {
            return;
        }
        this.showPricingConfirmDialog = true;
    }

    private async onSavePricingConfirmed() {
        try {
            this.isLoadingPricingDialog = true;
            const payload: Pricing = {
                baseFare: parseFloat(this.pricing.baseFare),
                perKm: parseFloat(this.pricing.perKm),
                perMinute: parseFloat(this.pricing.perMinute),
                bookingFee: parseFloat(this.pricing.bookingFee),
                sharedDiscountPercent: parseFloat(this.pricing.sharedDiscountPercent)
            };
            this.configuration = await this.setPricing(payload);
            this.pricing = this.pricingFromConfiguration();
            this.pricingServerErrors = {};
            this.showPricingConfirmDialog = false;
            this.$notifySuccessSimplified('SHARE.PRICING.SUCCESS');
        } catch (e) {
            // a 422 names the offending fields: the server's message goes under each
            this.pricingServerErrors = this.pricingErrorsOf(e);
            this.showPricingConfirmDialog = false;
            this.$notifyErrorSimplified('SHARE.PRICING.ERROR');
        } finally {
            this.isLoadingPricingDialog = false;
        }
    }

    private pricingErrorsOf(error: any): Partial<Record<PricingField, string>> {
        const errors: Partial<Record<PricingField, string>> = {};
        const entries = Array.isArray(error?.data) ? error.data : [];
        for (const entry of entries) {
            if (entry && PRICING_FIELDS.includes(entry.field) && typeof entry.message === 'string') {
                errors[entry.field as PricingField] = entry.message;
            }
        }
        return errors;
    }

    private get isIncentiveLive() {
        return !!this.configuration?.promoterIncentive?.effectiveFrom;
    }

    private get effectiveFromDisplay() {
        const value = this.configuration?.promoterIncentive?.effectiveFrom;
        return value ? moment(value).format('DD.MM.YYYY') : '';
    }

    /**
     * Switching the model on is the moment real liability starts being
     * recognised, so the confirmation says which of the two things is happening
     * rather than showing one generic message.
     */
    private get promoterIncentiveConfirmText() {
        const alreadyLive = !!this.configuration?.promoterIncentive?.effectiveFrom;
        if (this.incentiveLive && !alreadyLive) {
            return this.$t('SHARE.PROMOTER_INCENTIVE.CONFIRM.DESCRIPTION_GO_LIVE').toString();
        }
        if (!this.incentiveLive && alreadyLive) {
            return this.$t('SHARE.PROMOTER_INCENTIVE.CONFIRM.DESCRIPTION_PAUSE').toString();
        }
        return this.$t('SHARE.PROMOTER_INCENTIVE.CONFIRM.DESCRIPTION').toString();
    }

    private get hasPromoterIncentiveChanged() {
        return (this.configuration?.promoterIncentive?.signupBonus ?? 0).toString() !== this.signupBonus ||
            (this.configuration?.promoterIncentive?.perPassengerRate ?? 0).toString() !== this.perPassengerRate ||
            !!this.configuration?.promoterIncentive?.effectiveFrom !== this.incentiveLive;
    }
}
